<template>
  <div class="webrtc-video-box" ref="videoRef">
    <div class="monitor">
      <!-- 头部 -->
      <div class="r-top">
        <div class="r-top-code">{{ langMap.codeLabel[lang] }}：{{ currentRobot.code }}</div>
        <div class="r-top-controls">
          <!-- 摄像头切换下拉菜单 -->
          <Dropdown trigger="click" @on-click="handleCameraChange" placement="bottom-start" transfer>
            <div class="camera-dropdown-btn" :title="langMap.switchCamera && langMap.switchCamera[lang] || (langMap.selectView && langMap.selectView[lang]) || '切换视角'">
              <svg viewBox="0 0 1024 1024" version="1.1" xmlns="http://www.w3.org/2000/svg" width="1em" height="1em">
                <path d="M512 85.333333c235.648 0 426.666667 191.018667 426.666667 426.666667s-191.018667 426.666667-426.666667 426.666667S85.333333 747.648 85.333333 512 276.352 85.333333 512 85.333333z m0 64C311.466667 149.333333 149.333333 311.466667 149.333333 512s162.133333 362.666667 362.666667 362.666667 362.666667-162.133333 362.666667-362.666667S712.533333 149.333333 512 149.333333z m0 106.666667c141.376 0 256 114.624 256 256s-114.624 256-256 256-256-114.624-256-256 114.624-256 256-256z m0 64c-106.048 0-192 85.952-192 192s85.952 192 192 192 192-85.952 192-192-85.952-192-192-192z" fill="currentColor"></path>
              </svg>
              <span class="camera-label">{{ currentCameraLabel }}</span>
              <svg class="arrow-down" viewBox="0 0 1024 1024" version="1.1" xmlns="http://www.w3.org/2000/svg" width="0.7em" height="0.7em">
                <path d="M512 714.666667c-8.533333 0-17.066667-2.133333-23.466667-8.533334l-341.333333-341.333333c-12.8-12.8-12.8-32 0-44.8 12.8-12.8 32-12.8 44.8 0l320 317.866667 317.866667-320c12.8-12.8 32-12.8 44.8 0 12.8 12.8 12.8 32 0 44.8l-341.333334 341.333333c-4.266667 8.533333-12.8 10.666667-21.333333 10.666667z" fill="currentColor"></path>
              </svg>
            </div>
            <DropdownMenu slot="list">
              <DropdownItem name="0" :selected="currentCameraType === '0'" v-if="isCameraAvailable('0')">
                {{ langMap.cameraLeftFront[lang] }}
              </DropdownItem>
              <DropdownItem name="1" :selected="currentCameraType === '1'" v-if="isCameraAvailable('1')">
                {{ langMap.cameraLeftRear[lang] }}
              </DropdownItem>
              <DropdownItem name="2" :selected="currentCameraType === '2'" v-if="isCameraAvailable('2')">
                {{ langMap.cameraRightFront[lang] }}
              </DropdownItem>
              <DropdownItem name="3" :selected="currentCameraType === '3'" v-if="isCameraAvailable('3')">
                {{ langMap.cameraRightSide[lang] }}
              </DropdownItem>
              <DropdownItem name="4" :selected="currentCameraType === '4'" v-if="isCameraAvailable('4')">
                {{ langMap.cameraLeftFrontRightRear[lang] }}
              </DropdownItem>
              <DropdownItem name="5" :selected="currentCameraType === '5'" v-if="isCameraAvailable('5')">
                {{ langMap.cameraLeftRearRightFront[lang] }}
              </DropdownItem>
              <DropdownItem name="6" :selected="currentCameraType === '6'" v-if="isCameraAvailable('6')">
                {{ langMap.cameraPanoramicFront[lang] }}
              </DropdownItem>
              <DropdownItem name="7" :selected="currentCameraType === '7'" v-if="isCameraAvailable('7')">
                {{ langMap.cameraPanoramicRear[lang] }}
              </DropdownItem>
              <DropdownItem name="8" :selected="currentCameraType === '8'" v-if="isCameraAvailable('8')">
                {{ langMap.cameraInfraredLeftRear && langMap.cameraInfraredLeftRear[lang] || '红外左后' }}
              </DropdownItem>
              <DropdownItem name="9" :selected="currentCameraType === '9'" v-if="isCameraAvailable('9')">
                {{ langMap.cameraInfraredRightFront && langMap.cameraInfraredRightFront[lang] || '红外右前' }}
              </DropdownItem>
            </DropdownMenu>
          </Dropdown>
        </div>
        <div class="r-top-time">{{ time }}</div>
      </div>

      <!-- 视频区域 -->
      <div class="r-show-data">
        <div class="left-monitoring" ref="playerBox" @mousemove="onPlayerMouseMove">
          <div class="video-wrapper">
            <!-- WebRTC 视频元素 -->
            <video
              ref="videoPlayer"
              class="video-container"
              autoplay
              playsinline
              muted
            ></video>

            <!-- 播放按钮：对应 index.html Start 按钮，signalingReady 前显示为不可点击 -->
            <div class="start-play" v-if="rtcStatus === 'idle'" @click="startPlay" :class="{ disabled: !signalingReady }">
              <div class="play-icon">▶</div>
              <div>{{ langMap.clickToPlay[lang] }}</div>
            </div>

            <!-- 连接中状态 -->
            <div class="connecting-mask" v-if="rtcStatus === 'connecting'">
              <Spin fix>
                <Icon type="ios-loading" size="40" class="spin-icon"></Icon>
                <div class="connecting-text">{{ langMap.connecting[lang] }}</div>
              </Spin>
            </div>

            <!-- 缓冲中状态（已收到推流，但视频还未真正播放起来） -->
            <div class="connecting-mask" v-if="rtcStatus === 'buffering'">
              <Spin fix>
                <Icon type="ios-loading" size="40" class="spin-icon"></Icon>
                <div class="connecting-text">{{ langMap.buffering[lang] }}</div>
              </Spin>
            </div>

            <!-- 错误提示 -->
            <div class="stream-error-message" v-if="rtcStatus === 'error'">
              {{ langMap.streamErrorTip[lang] }}
              <button class="refresh-btn" @click="retryConnection">
                {{ langMap.refresh[lang] }}
              </button>
            </div>
          </div>

          <div
			class="player-toolbar"
			:class="{ 'is-fullscreen': isFullscreen, hidden: isFullscreen && !toolbarVisible }"
			v-if="rtcStatus === 'connected'"
		  >
            <div class="toolbar-left">
              <div
				class="toolbar-btn"
				@click="toggleMute"
				:title="isMuted ? langMap.workVision_player_unmute[lang] : langMap.workVision_player_mute[lang]"
			  >
                <span v-if="isMuted">🔇</span>
                <span v-else>🔊</span>
              </div>
              <input
                class="toolbar-volume"
                type="range"
                min="0"
                max="1"
                step="0.01"
                :value="volume"
                @input="onVolumeInput"
				:title="langMap.workVision_player_volume[lang] + '：' + Math.round(volume * 100) + '%'"
              />
              <span class="volume-text">{{ Math.round(volume * 100) }}%</span>
            </div>

            <div class="toolbar-right">
              <div
				class="toolbar-btn"
				@click="togglePause"
				:title="isPaused ? langMap.workVision_player_resume[lang] : langMap.workVision_player_pause[lang]"
			  >
                <span v-if="isPaused">▶</span>
                <span v-else>⏸</span>
              </div>
              <div class="toolbar-btn" @click="toggleFullscreen" :title="langMap.workVision_player_fullscreen[lang]">
                <span>⛶</span>
              </div>
              <div class="toolbar-btn danger" @click="stopPlay" :title="langMap.workVision_player_stop[lang]">
                <span>■</span>
              </div>
            </div>
        </div>
        </div>
      </div>

      <!-- 不在线提示 -->
      <div class="state" v-if="offline">
        <div style="margin:20% auto;width: 80%;text-align: center">
          <p style="font-size: 30px;color: #FFFFFF">{{ langMap.robotIsOffline[lang] }}</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import * as timeUtil from '@/utils/dateTimeUtil.js';
import api from '@/api';
import { connectWithUrl } from '@/utils/websocket.js';
import langMap from '@/utils/language';
import { mapState } from 'vuex';

export default {
  name: 'webrtc-video',
  props: {
    robotId: {
      type: Number,
      default: ''
    },
    useStun: {
      type: Boolean,
      default: true
    },
    iceServers: {
      type: Array,
      default: () => []
    },
    // 从父组件接收相机信息
    cameraInfo: {
      type: Object,
      default: () => ({ currentType: '' })
    }
  },
  data() {
    return {
      langMap,
      offline: false,
      time: '',
      currentRobot: {},

      robotCode: '',

      // 摄像头切换
      currentCameraType: '',
      availableCameras: [], // 可用的相机列表
      cameraTypeMap: {
        '0': 'cameraLeftFront',
        '1': 'cameraLeftRear',
        '2': 'cameraRightFront',
        '3': 'cameraRightSide',
        '4': 'cameraLeftFrontRightRear',
        '5': 'cameraLeftRearRightFront',
        '6': 'cameraPanoramicFront',
        '7': 'cameraPanoramicRear',
        '8': 'cameraInfraredLeftRear',
        '9': 'cameraInfraredRightFront'
      },

      // 定时器
      timer: null,

      // WebRTC 相关
      rtcStatus: 'idle',        // 'idle' | 'connecting' | 'buffering' | 'connected' | 'error'
      signalingReady: false,    // 信令 socket 已就绪（对应 Start 按钮从 disabled 变可点击）
      signalingSocket: null,
      pc: null,
      offerTimeout: null,       // 等待 offer 超时计时器
      // 错误后手动重试：等待新信令就绪后自动 startPlay
      pendingAutoStart: false,
      pendingAutoStartTimer: null,

      // 播放器控制
      isMuted: true,
      volume: 0,
      isPaused: false,
	  lastNonZeroVolume: 1,

	  // 全屏工具栏显示控制
	  isFullscreen: false,
	  toolbarVisible: true,
	  toolbarHideTimer: null,
	  
	  // 网络质量监控
	  statsInterval: null,
    };
  },
  computed: {
    ...mapState({
      lang: state => state.app.language,
    }),
    currentCameraLabel() {
      const labelMap = {
        '0': this.langMap.cameraLeftFront[this.lang],
        '1': this.langMap.cameraLeftRear[this.lang],
        '2': this.langMap.cameraRightFront[this.lang],
        '3': this.langMap.cameraRightSide[this.lang],
        '4': this.langMap.cameraLeftFrontRightRear[this.lang],
        '5': this.langMap.cameraLeftRearRightFront[this.lang],
        '6': this.langMap.cameraPanoramicFront[this.lang],
        '7': this.langMap.cameraPanoramicRear[this.lang],
        '8': this.langMap.cameraInfraredLeftRear && this.langMap.cameraInfraredLeftRear[this.lang] || '红外左后',
        '9': this.langMap.cameraInfraredRightFront && this.langMap.cameraInfraredRightFront[this.lang] || '红外右前'
      };
      return labelMap[this.currentCameraType] || this.langMap.selectView[this.lang];
    }
  },
  created() {
	this._onFullscreenChange = () => {
	  this.syncFullscreenState();
	};
	document.addEventListener('fullscreenchange', this._onFullscreenChange);
	document.addEventListener('webkitfullscreenchange', this._onFullscreenChange);
	this.syncFullscreenState();
  },
  watch: {
    robotId: {
      handler(newId) {
        if (newId) {
          this.getInfo(newId);
        }
      },
      immediate: true
    },
    robotCode(newCode) {
      if (!newCode) return;
      this.closePeerConnection();
      this.initSignaling();
    },
    // 监听从父组件传来的相机类型变化
    'cameraInfo.currentType': {
      handler(newType) {
        if (newType !== undefined && newType !== '') {
          this.currentCameraType = newType;
          console.log('相机视角已更新:', newType);
        }
      },
      immediate: true
    }
  },
  methods: {
    // 带时间戳的日志输出
    log(message, data = null) {
      const time = new Date().toLocaleTimeString('zh-CN', { hour12: false, hour: '2-digit', minute: '2-digit', second: '2-digit', fractionalSecondDigits: 3 });
      if (data) {
        console.log(`[${time}] [WebRTCVideo] ${message}`, data);
      } else {
        console.log(`[${time}] [WebRTCVideo] ${message}`);
      }
    },
    logError(message, data = null) {
      const time = new Date().toLocaleTimeString('zh-CN', { hour12: false, hour: '2-digit', minute: '2-digit', second: '2-digit', fractionalSecondDigits: 3 });
      if (data) {
        console.error(`[${time}] [WebRTCVideo] ${message}`, data);
      } else {
        console.error(`[${time}] [WebRTCVideo] ${message}`);
      }
    },
    logWarn(message, data = null) {
      const time = new Date().toLocaleTimeString('zh-CN', { hour12: false, hour: '2-digit', minute: '2-digit', second: '2-digit', fractionalSecondDigits: 3 });
      if (data) {
        console.warn(`[${time}] [WebRTCVideo] ${message}`, data);
      } else {
        console.warn(`[${time}] [WebRTCVideo] ${message}`);
      }
    },
    start() {
      timeUtil.startServerClock(this, (str) => {
        this.time = str;
      });
    },
    getInfo(id) {
      api.getData('robot-server/robot/get/' + id, this).then(res => {
        if (res.state == 0) {
          this.$Notice.error({ title: langMap.tip[this.lang], desc: res.msg });
        } else {
          this.currentRobot = res.data;
          this.robotCode = (res.data && res.data.code) ? res.data.code : '';
          if (res.data && res.data.cameraSwitchType !== undefined) {
            this.currentCameraType = res.data.cameraSwitchType;
          }
          // 加载相机配置列表
          this.loadCameraConfig(res.data.code);
        }
      });
    },
    // 加载相机配置
    loadCameraConfig(robotCode) {
      if (!robotCode) return;
      
      api.getData(`robot-server/camera/switch/config/get?robotCode=${robotCode}`, this).then(res => {
        if (res.state === 1 && res.data && res.data.cameraTypes) {
          // 解析相机类型字符串，例如 "0,2,4"
          this.availableCameras = res.data.cameraTypes.split(',').map(type => type.trim());
          this.log('相机配置加载成功', this.availableCameras);
          // 如果当前选中的相机不在可用列表中，清空选择让用户自己选
          if (this.currentCameraType && !this.availableCameras.includes(this.currentCameraType+'')) {
            this.currentCameraType = '';
          }
        } else {
          // 如果没有配置，使用默认列表
          this.availableCameras = ['0', '3', '4'];
        }
      }).catch(() => {
        // 加载失败时使用默认列表
        this.availableCameras = ['0', '3', '4'];
      });
    },

    // 检查相机是否可用
    isCameraAvailable(cameraType) {
      return this.availableCameras.includes(cameraType);
    },
    // ───────────── WebRTC 播放 ─────────────

    // 对应 index.html 页面加载时建立 WebSocket
    // signalingReady 对应 Start 按钮从 disabled 变为可点击
    initSignaling() {
      const clientId = this.generateRandomId(10);
      const signalingPath = `/signaling/${clientId}`;

      if (!this.robotCode) return;

      connectWithUrl(
        signalingPath,
        `WebRTCVideoSignaling_${this.robotCode}`,
        null,
        async (data) => {
          try {
            const message = JSON.parse(data);
            await this.handleSignalingMessage(message);
          } catch (e) {
            this.logError('处理信令消息失败:', e);
          }
        },
        (socket) => {
          // 对应 websocket.onopen → Start 按钮 disabled = false
          this.signalingSocket = socket;
          this.signalingReady = true;
          this.log('信令连接就绪');
          // 错误恢复路径：新 clientId 信令就绪后，自动 startPlay
          if (this.pendingAutoStart) {
            this.pendingAutoStart = false;
            this.clearPendingAutoStartTimer();
            this.$nextTick(() => this.startPlay());
          }
        },
        (err) => {
          this.logError('信令连接失败:', err);
          this.signalingReady = false;
          // 不改 rtcStatus，用户点击播放时再提示
          if (this.pendingAutoStart) {
            this.pendingAutoStart = false;
            this.clearPendingAutoStartTimer();
          }
        }
      );
    },

    // 对应 index.html 点击 Start 按钮 → start() → sendRequest()
    startPlay() {
      if (this.rtcStatus === 'connecting' || this.rtcStatus === 'connected') return;
      if (!this.robotCode) return;
      if (!this.signalingReady) {
        this.$Notice.error({
          title: this.langMap.tip[this.lang],
          desc: this.langMap.workVision_webrtc_signalingConnectError[this.lang]
        });
        return;
      }
      this.rtcStatus = 'connecting';
      this.signalingSocket.send(JSON.stringify({
        id: this.robotCode,
        type: 'request'
      }));
      this.log('发送 request, robotCode:', this.robotCode);

      // 等待 offer 超时处理
      this.offerTimeout = setTimeout(() => {
        if (this.rtcStatus === 'connecting') {
          console.error('[WebRTCVideo] 等待 offer 超时');
          this.clearOfferTimeout();
          this.rtcStatus = 'error';
          this.closePeerConnection();
          this.$Notice.error({
            title: this.langMap.tip[this.lang],
            desc: this.langMap.workVision_webrtc_deviceResponseTimeout[this.lang]
          });
        }
      }, 15000);
    },

    clearOfferTimeout() {
      if (this.offerTimeout) {
        clearTimeout(this.offerTimeout);
        this.offerTimeout = null;
      }
    },

    // 处理信令消息，与 client.js 的 websocket.onmessage 对应
    async handleSignalingMessage(message) {
      if (message.type === 'offer') {
        await this.handleOffer(message);
      } else if (message.type === 'error') {
        this.logError('收到信令错误:', message);
        this.rtcStatus = 'error';
        this.closePeerConnection();
      }
    },

    // 对应 client.js 的 handleOffer
    async handleOffer(offer) {
      // 收到 offer 说明设备已响应，清除超时
      this.clearOfferTimeout();
      
      this.log(`收到 Offer (SDP: ${offer.sdp ? offer.sdp.length : 0} 字节)`);
      
      try {
        const config = { bundlePolicy: 'max-bundle',iceCandidatePoolSize:0 };
        if (this.useStun) {
          config.iceServers = this.iceServers;
        }

        this.pc = new RTCPeerConnection(config);
        this.log('创建 PeerConnection' + (this.useStun ? ' (使用 STUN/TURN)' : ''));

        // ICE 候选收集监听（过滤 IPv6）
        let candidateCount = { host: 0, srflx: 0, relay: 0, ipv6Filtered: 0 };
        this.pc.addEventListener('icecandidate', (event) => {
          if (event.candidate) {
            // 过滤 IPv6 候选（地址包含冒号）
            if (event.candidate.address && event.candidate.address.includes(':')) {
              candidateCount.ipv6Filtered++;
              return; // 不处理此候选
            }
            
            // 统计候选类型
            const type = event.candidate.type;
            if (type === 'host') candidateCount.host++;
            else if (type === 'srflx') candidateCount.srflx++;
            else if (type === 'relay') candidateCount.relay++;
          } else {
            this.log(`ICE 收集完成 - host:${candidateCount.host} srflx:${candidateCount.srflx} relay:${candidateCount.relay} (过滤IPv6:${candidateCount.ipv6Filtered})`);
          }
        });

        // ICE 连接状态，与 client.js iceConnectionLog 对应
        this.pc.addEventListener('iceconnectionstatechange', () => {
          const state = this.pc.iceConnectionState;
          this.log('ICE 连接状态:', state);
          
          if (state === 'connected' || state === 'completed') {
            this.rtcStatus = 'connected';
            // 只在连接成功时打印候选对信息和 IP
            if (this.pc.getStats) {
              this.pc.getStats().then(stats => {
                let selectedPair = null;
                let localCandidates = new Map();
                let remoteCandidates = new Map();
                
                // 收集所有候选和候选对
                stats.forEach(report => {
                  if (report.type === 'local-candidate') {
                    localCandidates.set(report.id, report);
                  }
                  if (report.type === 'remote-candidate') {
                    remoteCandidates.set(report.id, report);
                  }
                  if (report.type === 'candidate-pair') {
                    // 优先选择 nominated，其次 succeeded
                    if (report.nominated || (!selectedPair && report.state === 'succeeded')) {
                      selectedPair = report;
                    }
                  }
                });
                
                // 显示连接信息
                if (selectedPair) {
                  const local = localCandidates.get(selectedPair.localCandidateId);
                  const remote = remoteCandidates.get(selectedPair.remoteCandidateId);
                  
                  if (local && remote) {
                    // 获取 IP，prflx 类型可能没有 address/ip，显示为 "未知"
                    const localAddr = local.address || local.ip || '';
                    const remoteAddr = remote.address || remote.ip || '';
                    const localIP = localAddr ? `${localAddr}:${local.port}` : `端口:${local.port}`;
                    const remoteIP = remoteAddr ? `${remoteAddr}:${remote.port}` : `端口:${remote.port}`;
                    
                    this.log(`✓ 连接成功 - ${local.candidateType}[${localIP}] <-> ${remote.candidateType}[${remoteIP}]`);
                  } else {
                    this.log('✓ 连接成功');
                  }
                } else {
                  this.log('✓ 连接成功');
                }
              });
            } else {
              this.log('✓ 连接成功');
            }
            // 开始监控网络质量
            this.startStatsMonitoring();
          } else if (state === 'failed') {
            this.logError('✗ ICE 连接失败');
            this.rtcStatus = 'error';
            this.closePeerConnection();
          } else if (state === 'disconnected') {
            this.logWarn('⚠ ICE 连接断开，等待重连...');
            // 给一些时间尝试重连，而不是立即关闭
            setTimeout(() => {
              if (this.pc && this.pc.iceConnectionState === 'disconnected') {
                this.logError('✗ ICE 重连超时');
                this.rtcStatus = 'error';
                this.closePeerConnection();
              }
            }, 5000);
          }
        });

        // 移除冗余的状态变化日志（只在关键状态输出）
        // this.pc.addEventListener('signalingstatechange', () => {
        //   console.log('[WebRTCVideo] 信令状态:', this.pc.signalingState);
        // });

        // 连接状态变化监听（只在关键状态输出）
        // this.pc.addEventListener('connectionstatechange', () => {
        //   console.log('[WebRTCVideo] 连接状态:', this.pc.connectionState);
        // });

        // 接收服务器推来的视频/音频轨道，与 client.js pc.ontrack 对应
        this.pc.ontrack = (evt) => {
          this.log('收到媒体流');
          // 已收到推流，先进入缓冲态，等 video 真正开始播放再进入 connected
          if (this.rtcStatus !== 'connected') {
            this.rtcStatus = 'buffering';
          }
          const video = this.$refs.videoPlayer;
          if (video) {
            video.srcObject = evt.streams[0];
            video.muted = this.isMuted;
            video.volume = this.volume;
            video.play().catch(e => {
              this.logWarn('自动播放被拦截，尝试静音播放');
              video.muted = true;
		      this.isMuted = true;
              video.play();
            });
          }
        };

        // 设置远端描述（服务器的 offer）
        await this.pc.setRemoteDescription(new RTCSessionDescription(offer));
        this.log('设置远端描述完成');

        // 创建并发送 answer
        await this.sendAnswer();
      } catch (e) {
        this.logError('处理 offer 失败:', e.message);
        this.rtcStatus = 'error';
        this.closePeerConnection();
      }
    },

    // 对应 client.js 的 sendAnswer
    async sendAnswer() {
      try {
        const answer = await this.pc.createAnswer();
        this.log('创建 Answer 完成');
        
        await this.pc.setLocalDescription(answer);

        // 等待至少收集到一些 ICE 候选（不等待全部完成，使用短暂超时）
        await this.waitForInitialIceCandidates();

        const answerData = {
          id: this.robotCode,
          type: this.pc.localDescription.type,
          sdp: this.pc.localDescription.sdp
        };
        
        this.signalingSocket.send(JSON.stringify(answerData));
        this.log(`Answer 已发送 (${this.pc.iceGatheringState})`);
      } catch (e) {
        this.logError('发送 answer 失败:', e);
        this.rtcStatus = 'error';
      }
    },

    // 对应 client.js 的 waitGatheringComplete
    waitForIceGathering() {
      return new Promise((resolve) => {
        if (this.pc.iceGatheringState === 'complete') {
          console.log('[WebRTCVideo] ICE 收集已完成，立即发送');
          resolve();
          return;
        }
        
        console.log('[WebRTCVideo] 等待 ICE 收集完成...');
        const timeout = setTimeout(() => {
          console.warn('[WebRTCVideo] ICE 收集超时（10秒），继续发送');
          console.log('[WebRTCVideo] 当前 ICE 收集状态:', this.pc.iceGatheringState);
          resolve();
        }, 10000);

        const handler = () => {
          console.log('[WebRTCVideo] ICE 收集状态变化:', this.pc.iceGatheringState);
          if (this.pc.iceGatheringState === 'complete') {
            clearTimeout(timeout);
            this.pc.removeEventListener('icegatheringstatechange', handler);
            console.log('[WebRTCVideo] ICE 收集完成');
            resolve();
          }
        };
        this.pc.addEventListener('icegatheringstatechange', handler);
      });
    },

    // 等待初始 ICE 候选（快速连接策略）
    waitForInitialIceCandidates() {
      return new Promise((resolve) => {
        // 如果已经完成收集，立即返回
        if (this.pc.iceGatheringState === 'complete') {
          resolve();
          return;
        }
        
        // 如果已经在收集中，等待短暂时间让更多候选进入 SDP
        if (this.pc.iceGatheringState === 'gathering') {
          setTimeout(resolve, 500);
          return;
        }
        
        // 状态为 'new'，等待开始收集或超时
        const timeout = setTimeout(() => {
          this.pc.removeEventListener('icegatheringstatechange', handler);
          resolve();
        }, 1000);

        const handler = () => {
          const state = this.pc.iceGatheringState;
          
          if (state === 'gathering') {
            // 开始收集了，再等待 500ms 让候选进入 SDP
            clearTimeout(timeout);
            this.pc.removeEventListener('icegatheringstatechange', handler);
            setTimeout(resolve, 500);
          } else if (state === 'complete') {
            // 直接完成了（罕见情况）
            clearTimeout(timeout);
            this.pc.removeEventListener('icegatheringstatechange', handler);
            resolve();
          }
        };
        
        this.pc.addEventListener('icegatheringstatechange', handler);
      });
    },

    // 对应 client.js 的 stop
    closePeerConnection() {
      this.stopStatsMonitoring();
      
      if (this.pc) {
        if (this.pc.getTransceivers) {
          this.pc.getTransceivers().forEach(t => t.stop && t.stop());
        }
        this.pc.close();
        this.pc = null;
      }
      if (this.signalingSocket) {
        this.signalingSocket.close();
        this.signalingSocket = null;
      }
      const video = this.$refs.videoPlayer;
      if (video) {
        video.onplaying = null;
	    video.onpause = null;
        video.srcObject = null;
	    video.muted = true;
	    video.volume = 0;
	    try {
	      video.pause();
	    } catch (e) {}
      }
	  this.isPaused = false;
	  this.isMuted = true;
	  this.volume = 0;
    },

	  stopPlay() {
	    this.clearOfferTimeout();
	    this.closePeerConnection();
	    this.rtcStatus = 'idle';
	    this.signalingReady = false;
	    this.initSignaling();
	  },

	  // 错误状态下用户点击「刷新」按钮：
	  // closePeerConnection 已把旧信令 socket 关闭，旧 clientId 也作废，
	  // 这里重置状态 + 用新 clientId 重新建立信令，等 onopen 就绪后自动 startPlay
	  retryConnection() {
	    this.clearOfferTimeout();
	    this.closePeerConnection();
	    this.rtcStatus = 'idle';
	    this.signalingReady = false;
	    this.pendingAutoStart = true;
	    this.clearPendingAutoStartTimer();
	    // 兜底：8s 内信令未就绪则放弃自动播放，提示用户
	    this.pendingAutoStartTimer = setTimeout(() => {
	      this.pendingAutoStartTimer = null;
	      if (this.pendingAutoStart) {
	        this.pendingAutoStart = false;
	        this.$Notice.error({
	          title: this.langMap.tip[this.lang],
	          desc: this.langMap.workVision_webrtc_signalingConnectError[this.lang]
	        });
	      }
	    }, 8000);
	    this.initSignaling();
	  },

	  clearPendingAutoStartTimer() {
	    if (this.pendingAutoStartTimer) {
	      clearTimeout(this.pendingAutoStartTimer);
	      this.pendingAutoStartTimer = null;
	    }
	  },

	  toggleMute() {
	    const video = this.$refs.videoPlayer;
	    if (!video) return;
	    if (this.isMuted) {
	      this.isMuted = false;
	      video.muted = false;
	      if (this.volume > 0) {
	        this.lastNonZeroVolume = this.volume;
	      }
	    } else {
	      if (this.volume > 0) {
	        this.lastNonZeroVolume = this.volume;
	      }
	      this.isMuted = true;
	      video.muted = true;
	    }
	  },

	  onVolumeInput(e) {
	    const v = Number(e && e.target ? e.target.value : 0);
	    this.volume = isNaN(v) ? 0 : Math.max(0, Math.min(1, v));
	    const video = this.$refs.videoPlayer;
	    if (video) {
	      video.volume = this.volume;
	    }
	    if (this.volume > 0) {
	      this.lastNonZeroVolume = this.volume;
	    }
	    if (this.volume === 0) {
	      this.isMuted = true;
	      if (video) video.muted = true;
	    } else {
	      this.isMuted = false;
	      if (video) video.muted = false;
	    }
	  },

	  togglePause() {
	    const video = this.$refs.videoPlayer;
	    if (!video) return;
	    if (video.paused) {
	      video.play();
	    } else {
	      video.pause();
	    }
	    this.isPaused = video.paused;
	  },

	  async toggleFullscreen() {
	    const el = this.$refs.playerBox;
	    if (!el) return;
	    const doc = document;
	    const isFs = doc.fullscreenElement || doc.webkitFullscreenElement;
	    try {
	      if (!isFs) {
	        const req = el.requestFullscreen || el.webkitRequestFullscreen;
	        req && (await req.call(el));
	      } else {
	        const exit = doc.exitFullscreen || doc.webkitExitFullscreen;
	        exit && (await exit.call(doc));
	      }
	    } catch (e) {
	      console.warn('[WebRTCVideo] 全屏切换失败:', e);
	    }
	  },

	  clearToolbarHideTimer() {
	    if (this.toolbarHideTimer) {
	      clearTimeout(this.toolbarHideTimer);
	      this.toolbarHideTimer = null;
	    }
	  },

	  scheduleToolbarAutoHide() {
	    this.clearToolbarHideTimer();
	    if (!this.isFullscreen) return;
	    this.toolbarHideTimer = setTimeout(() => {
	      this.toolbarVisible = false;
	    }, 2000);
	  },

	  onPlayerMouseMove() {
	    if (!this.isFullscreen) return;
	    this.toolbarVisible = true;
	    this.scheduleToolbarAutoHide();
	  },

	  syncFullscreenState() {
	    const doc = document;
	    const isFs = !!(doc.fullscreenElement || doc.webkitFullscreenElement);
	    this.isFullscreen = isFs;
	    if (this.isFullscreen) {
	      this.toolbarVisible = true;
	      this.scheduleToolbarAutoHide();
	    } else {
	      this.toolbarVisible = true;
	      this.clearToolbarHideTimer();
	    }
	  },

    // ───────────── 摄像头切换 ─────────────

    handleCameraChange(cameraType) {
      this.currentCameraType = cameraType;
      api.postData('notice-server/cmd/switch/camera', {
        code: this.currentRobot.code,
        data: { type: cameraType }
      }, this).then(res => {
        if (res.state == 0) {
          this.$Notice.error({
            title: langMap.tip[this.lang],
            desc: res.msg
          });
        } else {
          this.$Notice.success({
            title: langMap.success[this.lang],
            desc: res.message
          });
        }
      });
    },

    // ───────────── 工具函数 ─────────────

    generateRandomId(length) {
      const chars = '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz';
      return [...Array(length)].map(() => chars[Math.floor(Math.random() * chars.length)]).join('');
    },

    // 开始监控网络质量
    startStatsMonitoring() {
      this.stopStatsMonitoring();
      
      let lastBytesReceived = 0;
      let lastTimestamp = Date.now();
      let logCounter = 0;
      let isFirstLog = true; // 首次立即输出
      
      this.statsInterval = setInterval(async () => {
        if (!this.pc || this.pc.connectionState === 'closed') {
          this.stopStatsMonitoring();
          return;
        }
        
        try {
          const stats = await this.pc.getStats();
          const now = Date.now();
          const timeDiff = (now - lastTimestamp) / 1000; // 秒
          
          stats.forEach(report => {
            // 入站 RTP 流统计
            if (report.type === 'inbound-rtp' && report.kind === 'video') {
              const bytesReceived = report.bytesReceived || 0;
              const packetsLost = report.packetsLost || 0;
              const packetsReceived = report.packetsReceived || 0;
              const jitter = report.jitter || 0;
              
              // 计算比特率
              const bitrate = timeDiff > 0 ? ((bytesReceived - lastBytesReceived) * 8 / timeDiff / 1000).toFixed(2) : 0;
              
              // 计算丢包率
              const totalPackets = packetsReceived + packetsLost;
              const packetLossRate = totalPackets > 0 ? ((packetsLost / totalPackets) * 100).toFixed(2) : 0;
              
              const bitrateNum = parseFloat(bitrate);
              const lossNum = parseFloat(packetLossRate);
              const jitterNum = jitter * 1000;
              
              // 判断网络质量等级
              let qualityLevel = '优秀';
              let hasIssue = false;
              
              if (lossNum > 10 || bitrateNum < 100 || jitterNum > 200) {
                qualityLevel = '差';
                hasIssue = true;
              } else if (lossNum > 5 || bitrateNum < 500 || jitterNum > 100) {
                qualityLevel = '一般';
                hasIssue = true;
              } else if (lossNum > 2 || bitrateNum < 1000 || jitterNum > 50) {
                qualityLevel = '良好';
              }
              
              // 首次立即输出，之后每 5 秒输出一次，或异常时立即输出
              logCounter++;
              const shouldLog = isFirstLog || logCounter % 5 === 0;
              if (isFirstLog) isFirstLog = false;
              
              if (shouldLog || hasIssue) {
                const msg = `网络质量: ${qualityLevel} | 码率:${bitrate}kbps 丢包:${packetLossRate}% 抖动:${jitterNum.toFixed(0)}ms`;
                
                if (hasIssue) {
                  this.logWarn(msg);
                } else {
                  this.log(msg);
                }
              }
              
              lastBytesReceived = bytesReceived;
              lastTimestamp = now;
            }
          });

        } catch (err) {
          console.error('[WebRTCVideo] 获取统计信息失败:', err);
        }
      }, 5000); // 每5秒监控一次
    },
    
    stopStatsMonitoring() {
      if (this.statsInterval) {
        clearInterval(this.statsInterval);
        this.statsInterval = null;
      }
    }
  },

  mounted() {
    this.start();
    // 对应 index.html 页面加载就建立信令 WebSocket
    this.initSignaling();

    const video = this.$refs.videoPlayer;
    if (video) {
      video.onplaying = () => {
        if (this.rtcStatus === 'buffering' || this.rtcStatus === 'connecting') {
          this.rtcStatus = 'connected';
        }
      };

	  video.onpause = () => {
	    this.isPaused = true;
	  };
    }
  },

  beforeDestroy() {
    if (this.timer) clearInterval(this.timer);
    this.clearOfferTimeout();
    this.clearPendingAutoStartTimer();
    this.closePeerConnection();
	this.clearToolbarHideTimer();
	if (this._onFullscreenChange) {
	  document.removeEventListener('fullscreenchange', this._onFullscreenChange);
	  document.removeEventListener('webkitfullscreenchange', this._onFullscreenChange);
	}
  }
};
</script>

<style scoped lang="less">
.webrtc-video-box {
  height: 100%;
  width: 100%;
  border-radius: 5px;
  overflow: hidden;
  background-color: #000;
  position: relative;
}

.monitor {
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
}

// 头部
.r-top {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  padding: 10px 0;
  background-color: #000;
  color: #bab7b7;
  font-size: 20px;

  .r-top-code {
    padding-left: 10px;
    flex: 1;
  }

  .r-top-controls {
    display: flex;
    align-items: center;
    flex: 1;
    justify-content: center;
  }

  .r-top-time {
    flex-shrink: 0;
    padding-right: 35px;
    flex: 1;
    text-align: right;
  }
}

// 摄像头下拉按钮
.camera-dropdown-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  background: rgba(45, 140, 240, 0.1);
  border: 1px solid rgba(45, 140, 240, 0.3);
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.3s ease;
  color: #2d8cf0;
  white-space: nowrap;

  svg {
    width: 18px;
    height: 18px;
    color: #2d8cf0;
    flex-shrink: 0;
  }

  .camera-label {
    font-size: 14px;
    font-weight: 500;
  }

  .arrow-down {
    width: 12px;
    height: 12px;
    margin-left: 2px;
  }

  &:hover {
    background: rgba(45, 140, 240, 0.2);
    border-color: rgba(45, 140, 240, 0.5);
  }
}

// 数据区
.r-show-data {
  display: flex;
  flex: 1;
  background-color: #001529;
  height: calc(100% - 50px);

  .left-monitoring {
    height: 100%;
    width: 100%;
    display: flex;
    flex-direction: column;
    overflow: hidden;

	.video-wrapper {
	  position: relative;
	  flex: 1;
	  min-height: 0;
	  height: auto;
	}

    .video-container {
      width: 100%;
      height: 100%;
      background-color: #000;
      object-fit: contain;
    }
  }
}

// 播放按钮
.start-play {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  display: flex;
  flex-direction: column;
  align-items: center;
  color: #fff;
  cursor: pointer;
  z-index: 10;
  background: rgba(0, 0, 0, 0.5);
  padding: 20px;
  border-radius: 8px;

  .play-icon {
    font-size: 48px;
    margin-bottom: 10px;
  }

  div { font-size: 14px; }

  &:hover { background: rgba(0, 0, 0, 0.7); }

  // 信令未就绪时（对应 Start 按钮 disabled）
  &.disabled {
    opacity: 0.4;
    cursor: not-allowed;
    pointer-events: none;
  }
}

// 连接中遮罩
.connecting-mask {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.6);
  z-index: 10;

  .connecting-text {
    color: #fff;
    margin-top: 10px;
    font-size: 14px;
  }

  .spin-icon {
    animation: ani-spin 1s linear infinite;
  }
  @keyframes ani-spin {
    from { transform: rotate(0deg); }
    to   { transform: rotate(360deg); }
  }
}

.player-toolbar {
  width: 100%;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 12px;
	background: #000;
	border-top: 1px solid rgba(255, 255, 255, 0.12);
	flex-shrink: 0;
	transition: opacity 0.2s;

	&.hidden {
	  opacity: 0;
	  pointer-events: none;
	}

	&.is-fullscreen {
	  position: absolute;
	  left: 0;
	  bottom: 0;
	  z-index: 12;
	  border-top: none;
	  background: rgba(0, 0, 0, 0.72);
	}

  .toolbar-left,
  .toolbar-right {
    display: flex;
    align-items: center;
    gap: 10px;
  }

  .toolbar-btn {
    width: 34px;
    height: 34px;
    border-radius: 6px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #fff;
    cursor: pointer;
    user-select: none;
    background: rgba(255, 255, 255, 0.08);
    transition: background 0.2s;

    &:hover {
      background: rgba(255, 255, 255, 0.16);
    }

    &.danger {
      background: rgba(255, 59, 48, 0.75);

      &:hover {
        background: rgba(255, 59, 48, 1);
      }
    }
  }

  .toolbar-volume {
    width: 120px;
  }
  
  .volume-text {
    font-size: 12px;
    color: #fff;
    min-width: 32px;
    text-align: right;
    user-select: none;
  }
}

:fullscreen .left-monitoring {
  height: 100%;
  position: relative;
}

// 错误提示
.stream-error-message {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  color: #fff;
  background: rgba(0, 0, 0, 0.7);
  padding: 20px;
  border-radius: 8px;
  z-index: 10;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  text-align: center;
  width: 80%;
  max-width: 300px;

  .refresh-btn {
    background: #2d8cf0;
    border: none;
    color: #fff;
    padding: 8px 16px;
    border-radius: 4px;
    cursor: pointer;
    font-size: 14px;
    &:hover { opacity: 0.8; }
  }
}

// 不在线提示
.state {
  position: absolute;
  bottom: 0;
  left: 0;
  background-color: #000c17;
  width: 100%;
  height: 100%;
  z-index: 10000;
}
</style>
